import { z } from "zod";
import { Form } from "@/components/ui/form";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { Diff } from "@/features/FormCard/Diff";
import {
  dateSchema,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";
import { Tables } from "@/gen/tableNames/tables";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  quantity: z.coerce.number().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
const formSchema = FormSchema.keyof().enum;

export function C3PositionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation([Tables.C3_POSITIONS, "tables"]);

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({ id, client, table_name, FormSchema });

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Position",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.strategy_from_id}
                    label={t(`${table_name}.columns.strategy_from_id`, {
                      ns: "tables",
                      defaultValue: "Stratégie (depuis)",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.strategy_to_id}
                    label={t(`${table_name}.columns.strategy_to_id`, {
                      ns: "tables",
                      defaultValue: "Stratégie (vers)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchema.date}
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.quantity}
                    label={t(`${table_name}.columns.quantity`, {
                      ns: "tables",
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.weight}
                    label={t(`${table_name}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Poids",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.market_value_local_ccy}
                    label={t(`${table_name}.columns.market_value_local_ccy`, {
                      ns: "tables",
                      defaultValue: "Valeur marché (devise locale)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.market_value_ptf_ccy}
                    label={t(`${table_name}.columns.market_value_ptf_ccy`, {
                      ns: "tables",
                      defaultValue: "Valeur marché (devise ptf)",
                    })}
                    control={form.control}
                  />
                </div>

              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? Diff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
